// „Продадени продукти" — теоретично срещу реално, чистата логика: обединява
// теоретичното (`/sold-products` — текущата рецепта × продаденото по текущата
// ср. цена) с реално изписаното от разнасянията (`/sold-products/posted`) и
// смята разликата РЕАЛНО − ТЕОРЕТИЧНО (к-во и стойност), сумите на колоните,
// кога разликата е нула и склада, с който се отваря складовата карта.
// Изгледът е в `SoldProductsTable.tsx` / `SoldProductPanel.tsx`.
import type { SoldSellable } from "../../../../api/inventory";
import type { SoldPostedProduct, SoldPostedWarehouse } from "../../../../api/inventorySoldAnalysis";
import { formatMoney } from "../../components/numbers";
import { formatQty } from "../../components/qty";
import { compareText } from "../components/reportListModel";

// Реалното на продукта. Физически от склада е излязло `quantity` = изписаното
// от самите разнасяния + изписаното ВМЕСТО друг продукт; покритото от друг
// продукт НЕ е изписано — то само обяснява защо реалното е по-малко.
export type SoldActual = {
  quantity: number;
  valueNoVat: number;
  valueWithVat: number;
  postedQuantity: number;
  donorQuantity: number;
  coveredQuantity: number;
  postingsCount: number;
  warehouses: SoldPostedWarehouse[];
};

// Разнесено − по рецепта: „+" — изписано е повече, отколкото казват рецептите
export type SoldVariance = { quantity: number; valueNoVat: number; valueWithVat: number };

// Ред на справката: теоретичното + (само при продуктите) реалното и разликата.
// `actual` е null при рецептите и докато реалното се зарежда.
export type SoldRow = SoldSellable & { actual: SoldActual | null; variance: SoldVariance | null };

const ZERO_ACTUAL: SoldActual = {
  quantity: 0,
  valueNoVat: 0,
  valueWithVat: 0,
  postedQuantity: 0,
  donorQuantity: 0,
  coveredQuantity: 0,
  postingsCount: 0,
  warehouses: [],
};

// Сумите — до стотинка, количествата — до 4 знака (колкото пази BE), за да не
// излиза „разлика 0,0000001" от събирането на float-ове
const roundTo = (value: number, digits: number) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

export const actualOf = (posted: SoldPostedProduct): SoldActual => ({
  quantity: roundTo(posted.postedQuantity + posted.donorQuantity, 4),
  valueNoVat: roundTo(posted.postedValueNoVat + posted.donorValueNoVat, 2),
  valueWithVat: roundTo(posted.postedValueWithVat + posted.donorValueWithVat, 2),
  postedQuantity: posted.postedQuantity,
  donorQuantity: posted.donorQuantity,
  coveredQuantity: posted.coveredQuantity,
  postingsCount: posted.postingsCount,
  warehouses: posted.warehouses,
});

export const varianceOf = (theoretical: SoldSellable, actual: SoldActual): SoldVariance => ({
  quantity: roundTo(actual.quantity - theoretical.quantity, 4),
  valueNoVat: roundTo(actual.valueNoVat - theoretical.sumNoVat, 2),
  valueWithVat: roundTo(actual.valueWithVat - theoretical.totalSum, 2),
});

export const asSoldRows = (rows: SoldSellable[]): SoldRow[] =>
  rows.map((row) => ({ ...row, actual: null, variance: null }));

// Продукт само в реалното: рецептата е сменена след разнасянето, продуктът е
// само заместител или сметките са извън периода (отчет след полунощ) —
// теоретичното му е 0 и цялото изписано е разлика.
const onlyPostedRow = (posted: SoldPostedProduct): SoldRow => {
  const theoretical: SoldSellable = {
    sellableId: posted.productId,
    name: posted.name,
    quantity: 0,
    avgPrice: 0,
    totalSum: 0,
    sumNoVat: 0,
    ingredientsCount: 0,
    unit: posted.unit,
  };
  const actual = actualOf(posted);
  return { ...theoretical, actual, variance: varianceOf(theoretical, actual) };
};

// Обединението: всеки продукт от теоретичното ИЛИ от реалното. Без реално
// (още се зарежда) — теоретичните редове с `actual: null`, без разлика.
export const mergeSoldProducts = (
  theoretical: SoldSellable[],
  posted: SoldPostedProduct[] | undefined,
): SoldRow[] => {
  if (!posted) return asSoldRows(theoretical);
  const postedById = new Map(posted.map((item) => [item.productId, item]));
  const rows: SoldRow[] = theoretical.map((row) => {
    const match = postedById.get(row.sellableId);
    const actual = match ? actualOf(match) : ZERO_ACTUAL;
    return { ...row, actual, variance: varianceOf(row, actual) };
  });
  const known = new Set(theoretical.map((row) => row.sellableId));
  const extra = posted.filter((item) => !known.has(item.productId)).map(onlyPostedRow);
  // BE подрежда по име — добавените се вмъкват на мястото си
  return extra.length ? [...rows, ...extra].sort((a, b) => compareText(a.name, b.name)) : rows;
};

// Ред без нищо: продадено и сторнирано до 0 и нищо изписано — не се показва
// (тире навсякъде, без информация). Продукт само с реално изписано остава.
export const isEmptySoldRow = (row: SoldRow) =>
  isZeroQuantity(row.quantity, 3) &&
  isZeroMoney(row.totalSum) &&
  (!row.actual || (isZeroQuantity(row.actual.quantity, 3) && isZeroMoney(row.actual.valueWithVat)));

// ─── Нула ли е разликата ─────────────────────────────────────────────────
// Количество: под половин единица от последния показан знак (при 3 знака —
// под 0,0005) се чете като 0, иначе „разлика 0,000" стои като разминаване.
export const isZeroQuantity = (value: number, decimals: number) =>
  Math.abs(value) < 0.5 * 10 ** -Math.max(0, decimals);

export const isZeroMoney = (value: number) => Math.abs(value) < 0.005;

// Разликата със знак: „+0,600" / „−0,600" (типографски минус, както в
// складовата карта); нулата е null — клетката показва тире
export const formatSignedQuantity = (value: number, decimals: number) =>
  isZeroQuantity(value, decimals) ? null : `${value > 0 ? "+" : "−"}${formatQty(Math.abs(value), decimals)}`;

export const formatSignedMoney = (value: number) =>
  isZeroMoney(value) ? null : `${value > 0 ? "+" : "−"}${formatMoney(Math.abs(value))}`;

// Редовете на подсказката за замяна: колко е изписано ВМЕСТО друг продукт и
// колко от нуждата е покрито от друг — те обясняват част от разликата
export const substitutionLines = (
  actual: SoldActual | null,
  decimals: number,
  unit: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  if (!actual) return [];
  const qty = (value: number) => `${formatQty(value, decimals)}${unit ? ` ${unit}` : ""}`;
  return [
    actual.donorQuantity > 0 && t("inventoryPage.sdDonorLine", { qty: qty(actual.donorQuantity) }),
    actual.coveredQuantity > 0 && t("inventoryPage.sdCoveredLine", { qty: qty(actual.coveredQuantity) }),
  ].filter((line): line is string => !!line);
};

// Има ли замяна, която обяснява част от разликата (⇄ в реда)
export const hasSubstitution = (actual: SoldActual | null) =>
  !!actual && (actual.donorQuantity > 0 || actual.coveredQuantity > 0);

// ─── Сумите на колоните (стойностите са бруто — както „Бруто" досега) ─────
export type SoldVarianceTotals = {
  theoreticalGross: number;
  actualGross: number;
  varianceGross: number;
  actualLoaded: boolean;
};

export const soldVarianceTotals = (rows: SoldRow[]): SoldVarianceTotals => {
  const theoreticalGross = rows.reduce((sum, row) => sum + (row.totalSum || 0), 0);
  const actualGross = rows.reduce((sum, row) => sum + (row.actual?.valueWithVat ?? 0), 0);
  return {
    theoreticalGross: roundTo(theoreticalGross, 2),
    actualGross: roundTo(actualGross, 2),
    varianceGross: roundTo(actualGross - theoreticalGross, 2),
    actualLoaded: rows.length === 0 || rows.every((row) => row.actual !== null),
  };
};

// ─── Складът на складовата карта ──────────────────────────────────────────
// „Продадени" не е по склад, картата — е. Отваря се в склада, от който
// продуктът е изписан най-много през периода (там са разнасянията, които
// обясняват разликата); без такъв — складът от конфигурацията, иначе първият.
export const cardWarehouseOf = (
  actual: SoldActual | null,
  configuredWarehouseId: number | null,
  warehouseIds: number[],
): number | null => {
  const main = actual?.warehouses.find((item) => item.quantity > 0)?.warehouseId;
  if (main != null) return main;
  if (configuredWarehouseId != null && warehouseIds.includes(configuredWarehouseId)) return configuredWarehouseId;
  return warehouseIds[0] ?? configuredWarehouseId ?? null;
};
